import { useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { type KeyMoment, emotionMeta } from './types';

export interface WaveformHandle { seekTo: (sec: number) => void }

const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

/**
 * פס קול חזותי אמיתי (wavesurfer) המסונכרן לגרף הרגשות: נגן + waveform +
 * סמני רגעי מפתח על ציר הזמן. חושף seekTo כדי שלחיצה בגרף/בצ'יפ תקפיץ לזמן.
 */
export const CallWaveform = forwardRef<WaveformHandle, {
  src: string; keyMoments?: KeyMoment[]; duration?: number;
}>(function CallWaveform({ src, keyMoments = [], duration }, ref) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const wsRef = useRef<WaveSurfer | null>(null);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(duration ?? 0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;
    const ws = WaveSurfer.create({
      container: containerRef.current,
      height: 56,
      waveColor: '#c7cdd6',
      progressColor: '#2563eb',
      cursorColor: '#2563eb',
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
      normalize: true,
    });
    wsRef.current = ws;
    ws.load(src).catch(() => setFailed(true));
    ws.on('ready', () => { setReady(true); setDur(ws.getDuration()); });
    ws.on('timeupdate', (t) => setCur(t));
    ws.on('play', () => setPlaying(true));
    ws.on('pause', () => setPlaying(false));
    ws.on('finish', () => setPlaying(false));
    ws.on('error', () => setFailed(true));
    return () => { ws.destroy(); wsRef.current = null; };
  }, [src]);

  useImperativeHandle(ref, () => ({
    seekTo: (sec: number) => {
      const ws = wsRef.current;
      if (!ws || !dur) return;
      ws.seekTo(Math.min(1, Math.max(0, sec / dur)));
      void ws.play();
    },
  }), [dur]);

  if (failed) {
    return <a href={src} download target="_blank" rel="noreferrer" className="btn">הורד הקלטה</a>;
  }

  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', padding: 8, background: 'var(--surface)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button type="button" className="btn" onClick={() => wsRef.current?.playPause()} disabled={!ready} style={{ minWidth: 40 }}>
          {playing ? '⏸' : '▶'}
        </button>
        <div style={{ position: 'relative', flex: 1 }}>
          <div ref={containerRef} />
          {/* סמני רגעי מפתח על ציר הזמן */}
          {ready && dur > 0 && keyMoments.map((k, i) => {
            const m = emotionMeta(k.emotion);
            return (
              <button key={i} type="button" title={`${fmt(k.t)} · ${k.phrase}`}
                onClick={() => { const ws = wsRef.current; if (ws) { ws.seekTo(Math.min(1, k.t / dur)); void ws.play(); } }}
                style={{ position: 'absolute', top: -6, left: `${Math.min(100, (k.t / dur) * 100)}%`, transform: 'translateX(-50%)', fontSize: 12, lineHeight: 1, cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}>
                {m.emoji}
              </button>
            );
          })}
        </div>
        <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', minWidth: 78, textAlign: 'end' }} dir="ltr">
          {fmt(cur)} / {fmt(dur)}
        </span>
      </div>
    </div>
  );
});
